/**
 * Search-param coercion for the trade routes, extracted so the producer logic
 * that drives the LR-478 origin-aware breadcrumb is directly unit-testable
 * (router.tsx itself is not imported by tests; LR-478 review r6).
 */
import { parsePositiveIntParam } from '@/utils/search-params';

/** Where the single-trade page was opened from — the breadcrumb root + return target. */
export type TradeOrigin = 'trades' | 'mtm' | 'history';

export interface TradeDetailSearch {
  from?: TradeOrigin;
  cp?: number;
}

/**
 * The origin for a surface that is NOT in the `TradeOrigin` whitelist: nothing is
 * stamped on the search, so the trade page's back control falls back to "Trades"
 * (`from: undefined`, the documented contract below). Billing's two links pass this
 * (LR-613) - a real Back-to-Billing needs a conditional back target in TradeHeader,
 * whose `to` is hardcoded to /trades, and was left out of that ticket's scope.
 * Lives here so the day a surface earns its own origin, the whitelist and this
 * fallback are read together instead of in a per-file copy.
 */
export const NO_TRADE_ORIGIN: TradeDetailSearch = {};

/**
 * Counterparty-id search coercion shared by /trades, /trades/$tradeId and
 * /collateral (LR-587) — a positive safe integer, else undefined.
 *
 * Deliberately NOT `Number.isFinite(Number(value))`: the router hands
 * `validateSearch` JSON, so that form accepted `?cp=true` as counterparty 1 and
 * `?cp=["5"]` as 5, then MtmPage normalised the URL to the id it guessed. Same
 * rule as the billing filters, shared so the two cannot drift.
 */
export const parseCp = parsePositiveIntParam;

/**
 * The Trades counterparty tab: the counterparty id, or (a link made before tabs had ids) the
 * tab's slug, honoured for one release. The router JSON-parses the query, so `?counterparty=7`
 * arrives as a number.
 */
export const parseCounterpartyTab = (value: unknown): number | string | undefined =>
  parsePositiveIntParam(value) ?? (typeof value === 'string' && value !== '' ? value : undefined);

/**
 * LR-498: History master–detail left-rail view. `date` = chronological blotter
 * (all CPs), `cp` = one counterparty's history. Strict whitelist; anything else
 * → undefined so HistoryShell falls back to the tab's natural default
 * (mtm → 'date', cashpool → 'cp').
 */
export const parseHview = (value: unknown): 'date' | 'cp' | undefined =>
  value === 'date' ? 'date' : value === 'cp' ? 'cp' : undefined;

/**
 * LR-498: the History deep-link selection key. Free string — a run/margin-call id
 * (date mode) or a cp-mode key: `"<counterparty_name>|<currency>"` on Mark to Market, the
 * counterparty name on Cash Pool (one repo book per counterparty). Coercion kept
 * here (not inlined in validateSearch) so it is unit-testable like parseCp.
 * The router JSON-parses the query, so a hand-typed `?sel=9532` arrives as a number (the
 * screen itself writes `?sel="9532"`). Any present value is kept as a string, an id in
 * canonical form (`09532` → `9532`): one that names no row must reach HistoryShell, which
 * says so, instead of vanishing into the default selection.
 */
export const parseSel = (value: unknown): string | undefined => {
  if (value === undefined || value === '') return undefined;
  const s = typeof value === 'string' ? value : JSON.stringify(value);
  return parsePositiveIntParam(s)?.toString() ?? s;
};

/**
 * LR-671: the saved-view id on /trades (`?view_id=`, same name as /inventory —
 * the shared saved-views module and Copy Link rely on it). Positive safe
 * integer, else undefined, so a hand-edited `?view_id=0` / `?view_id=abc`
 * falls back to the default-view branch of use-trades-view-bootstrap instead
 * of being loaded as an id. Same coercion as `parseCp`, kept here so it is
 * unit-testable rather than inlined in validateSearch.
 */
export const parseViewId = parsePositiveIntParam;

/**
 * Narrow a raw query object to the trade-detail breadcrumb signal. `from` is a
 * strict whitelist (anything unrecognised → undefined → breadcrumb falls back to
 * "Trades"); `cp` carries the MTM counterparty scope.
 */
export const parseTradeDetailSearch = (search: Record<string, unknown>): TradeDetailSearch => ({
  from:
    search.from === 'mtm'
      ? 'mtm'
      : search.from === 'history'
        ? 'history'
        : search.from === 'trades'
          ? 'trades'
          : undefined,
  cp: parseCp(search.cp),
});
